<template>
  <v-container fluid>
    <v-alert v-if="erreur" outlined text type="error">
      {{ $t("loading_error") }}
    </v-alert>
    <v-row>
      <v-col>
        <v-card elevation="0" class="mt-12">
          <v-card-title>
            {{ $t("data_query") }}
          </v-card-title>
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-header>
                <v-row no-gutters>
                  <v-col cols="6">
                    {{ $tc("mortality_measure", 0) }}
                  </v-col>
                  <v-col cols="6" class="text--secondary">
                    <v-fade-transition leave-absolute>
                      <v-row no-gutters style="width: 100%">
                        <v-col cols="2"></v-col>
                        <v-col cols="10">
                          {{
                            $i18n.tc(formulaire.measures, 0) || $t("not_set")
                          }}
                        </v-col>
                      </v-row>
                    </v-fade-transition>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <MeasuresForm @update-measures="updateMeasures" />
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header>
                <v-row no-gutters>
                  <v-col cols="6">
                    {{ $tc("death_year", 2) }}
                  </v-col>
                  <v-col cols="6" class="text--secondary">
                    <v-fade-transition leave-absolute>
                      <v-row no-gutters style="width: 100%">
                        <v-col cols="2"></v-col>
                        <v-col
                          cols="10"
                          v-if="
                            formulaire.time_scope.range_1.date_1 !==
                              formulaire.time_scope.range_1.date_2
                          "
                        >
                          {{ $t("from") }}
                          {{ formulaire.time_scope.range_1.date_1 || "?" }}
                          {{ $t("to") }}
                          {{ formulaire.time_scope.range_1.date_2 || "?" }}
                        </v-col>
                        <v-col cols="10" v-else>
                          {{ formulaire.time_scope.range_1.date_1 || "?" }}
                        </v-col>
                      </v-row>
                    </v-fade-transition>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <DateRangeForm :cim="this.cim" @update-date="updateDate" />
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header>
                <v-row no-gutters>
                  <v-col cols="6">
                    {{ $t("sex") }}
                  </v-col>
                  <v-col cols="6" class="text--secondary">
                    <v-fade-transition leave-absolute>
                      <v-row no-gutters style="width: 100%">
                        <v-col cols="2"></v-col>
                        <v-col cols="10" v-if="formulaire.sex.length > 0">
                          {{
                            formulaire.sex
                              .map(function(item) {
                                return $t("sex_modality." + item);
                              })
                              .join(", ") || ""
                          }}
                        </v-col>
                        <v-col cols="10" v-else>
                          {{ $t("sex_modality.Both") }}
                        </v-col>
                      </v-row>
                    </v-fade-transition>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <SexesForm 
                :measures="formulaire.measures || 'raw_number_of_death'"
                @update-sex="updateSex" />
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header>
                <v-row no-gutters>
                  <v-col cols="6">
                    {{ $t("age") }}
                  </v-col>
                  <v-col cols="6" class="text--secondary">
                    <v-fade-transition leave-absolute>
                      <v-row no-gutters style="width: 100%">
                        <v-col cols="2"></v-col>
                        <v-col cols="10" v-if="formulaire.age">
                          {{ agePanelHeader }}
                        </v-col>
                        <v-col cols="10" v-else>
                          {{ $t("not_included") }}
                        </v-col>
                      </v-row>
                    </v-fade-transition>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <AgeForm
                  :measures="formulaire.measures || 'raw_number_of_death'"
                  :agregration_1_24="agregration_1_24"
                  @update-age="updateAge"
                />
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header>
                <v-row no-gutters>
                  <v-col cols="6">
                    {{ $tc("location", 2) }}
                  </v-col>
                  <v-col cols="6" class="text--secondary">
                    <v-fade-transition leave-absolute>
                      <v-row no-gutters style="width: 100%">
                        <v-col cols="2"></v-col>
                        <v-col cols="10" v-if="formulaire.geo.selectedGeoClass">
                          {{ geoPanelHeader }}
                        </v-col>
                        <v-col cols="10" v-else>
                          {{ $t("france_metropolitaine_et_drom") }}
                        </v-col>
                      </v-row>
                    </v-fade-transition>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <GeoForm :lang="this.$i18n.locale" @update-geo="updateGeo" />
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header>
                <v-row no-gutters>
                  <v-col cols="6">
                    {{ $tc("cause_of_death", 2) }}
                  </v-col>
                  <v-col cols="6" class="text--secondary">
                    <v-fade-transition leave-absolute>
                      <v-row no-gutters style="width: 100%">
                        <v-col cols="2"></v-col>
                        <v-col cols="10" v-if="formulaire.cod.label">
                          {{ codPanelHeader }}
                        </v-col>
                        <v-col cols="10" v-else>
                          {{ $t("all_causes") }}
                        </v-col>
                      </v-row>
                    </v-fade-transition>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <EurostatCodForm
                  :lang="this.$i18n.locale"
                  @update-cod="updateCod"
                />
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
          <v-card-actions class="justify-center">
            <v-btn
              :disabled="!isValid"
              color="primary"
              outlined
              class="mt-3"
              @click="
                telechargement = false;
                calcul();
              "
            >
              {{ $t("submit") }}
              <v-icon right>{{ mdiMenuRight }}</v-icon>
            </v-btn>
            <v-btn
              :disabled="!isValid"
              color="primary"
              outlined
              class="mt-3"
              @click="
                telechargement = false;
                erreur_dimension_max = false;
                attente_telechargement = true;
                downloadCsvData();
              "
            >
              {{ $t("download") }}
              <v-icon right small>{{ mdiDownload }}</v-icon>
            </v-btn>
          </v-card-actions>
          <br />
        </v-card>
      </v-col>
      <v-col>
        <v-card elevation="0" class="mt-12">
          <v-card-title>
            {{ $tc("result", 1) }}
          </v-card-title>
            <v-alert
              v-model="provisional_mortality_rate"
              outlined
              text
              type="warning"
            >
            {{ this.$t('alert.message_provisional_mortality_rate') }}
            </v-alert>
            <v-alert
              v-model="attente_telechargement"
              outlined
              text
              type="info"
              dismissible
            >
              {{ $t("download_about_to_start") }}
            </v-alert>
            <v-alert
              v-model="telechargement"
              outlined
              text
              type="success"
              dismissible
            >
              {{ $t("download_in_progress") }}
            </v-alert>
            <v-alert
              v-model="erreur_dimension_max"
              outlined
              text
              type="warning"
              dismissible
            >
              {{ this.message_dimension_max }}
            </v-alert>
          <v-card-text>
            <v-row id="results-in-plain-text" v-if="items">
              <template>
                <v-data-table
                  :headers="tableheaders(this.items)"
                  :items="items"
                  :items-per-page="10"
                  :options.sync="datatableOptions"
                  class="elevation-1"
                  multi-sort
                  loading-text="$t('loading')"
                  :footer-props="{
                    showFirstLastPage: true,
                    itemsPerPageOptions: [5, 10, 15, 50, 100]
                  }"
                ></v-data-table>
              </template>
            </v-row>
          </v-card-text>
        </v-card>
        <v-card elevation="0" class="mt-12" v-if="items_cod">
          <v-card-title>
            {{ $t("metadata") }}
          </v-card-title>
          <v-card-text>
            <br />
            <v-row id="cod-metadata-title" v-if="items_cod">
              <a
                :href="this.$t('cod_2012.src')"
                :title="this.$t('cod_2012.src')"
                target="_blank"
                class="warning--text"
                >{{ this.$t("cod_2012.label") }}
                <v-icon small color="warning">{{ mdiOpenInNew }}</v-icon>
              </a>
              {{ " :" }}
            </v-row>
            <br />
            <v-row id="cod-metadata-in-plain-text" v-if="items_cod">
              <template>
                <v-data-table
                  :headers="tableheaders(this.items_cod)"
                  :items="items_cod"
                  :items-per-page="5"
                  loading-text="$t('loading')"
                ></v-data-table>
              </template>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mdiMenuRight, mdiDownload, mdiOpenInNew } from "@mdi/js";
import axios from "axios";
import MeasuresForm from "@/components/MeasuresForm.vue";
import DateRangeForm from "@/components/DateRangeForm.vue";
import SexesForm from "@/components/SexesForm.vue";
import AgeForm from "@/components/AgeForm.vue";
import GeoForm from "@/components/GeoForm.vue";
import EurostatCodForm from "@/components/EurostatCodForm.vue";

export default {
  name: "ComplexQuery",
  components: {
    MeasuresForm,
    DateRangeForm,
    SexesForm,
    AgeForm,
    GeoForm,
    EurostatCodForm
  },
  props: {
    agregration_1_24: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: function() {
    return {
      panel: 0,
      cim: function() {
        return ["9", "10"];
      },
      datatableOptions: {
        page: 1
      },
      formulaire: {
        time_scope: {
          range_1: {
            date_1: 2017,
            date_2: 2022,
            byYear: true
          },
          range_2: {
            date_1: null,
            date_2: null,
            byYear: true
          },
          range_3: {
            date_1: null,
            date_2: null,
            byYear: true
          }
        },
        measures: "raw_number_of_death",
        standpop: null,
        sex: ["12"],
        age: {
          age: false,
          selectedAgeClassLength: null,
          ageFilter: false,
          selectedAge: null
        },
        geo: {
          selectedGeoClass: "france_metropolitaine_et_drom",
          selectedGeo: {
            code: null,
            label: null
          }
        },
        cod: {
          code: null,
          label: null
        }
      },
      mdiMenuRight: mdiMenuRight,
      mdiDownload: mdiDownload,
      mdiOpenInNew: mdiOpenInNew,
      rules: [value => !!value || this.$t("mandatory")],
      items: null,
      items_cod: null,
      valid: {
        measures: true,
        sex: true,
        cod: true
      },
      erreur: false,
      erreur_dimension_max: false,
      message_dimension_max: "",
      provisional_mortality_rate: false,
      attente_telechargement: false,
      telechargement: false
    };
  },
  computed: {
    isValid: function() {
      return this.valid.measures && this.formulaire.measures !== undefined;
    },
    agePanelHeader: function() {
      let el = this.formulaire.age;
      const val = this;
      if (this.formulaire.measures === undefined) {
        return val.$t("age_modality.all_ages");
      } else {
        if (el.age === null || el.age === false) {
            return val.$t("age_modality.all_ages");
          } else {
            if (el.ageFilter) {
              if (el.selectedAge.length > 0) {
                if (el.selectedAge.length > 4) {
                  return (
                    el.selectedAge
                      .map(function(item) {
                        return val.$i18n.t("age_modality." + item);
                      })
                      .slice(0, 4)
                      .join(", ") + ", ..."
                  );
                } else {
                  return el.selectedAge
                    .map(function(item) {
                      return val.$i18n.t("age_modality." + item);
                    })
                    .join(", ");
                }
              } else {
                return val.$t("age_modality.all_ages");
              }
            } else {
              if (
                el.selectedAge.length > 0 &&
                el.selectedAgeClassLength !== undefined
              ) {
                return val.$tc(el.selectedAgeClassLength, 2);
              } else {
                if (el.age && el.selectedAgeClassLength !== undefined) {
                  return val.$tc(el.selectedAgeClassLength, 2);
                } else {
                  return val.$t("age_modality.all_ages");
                }
            }
          }
        }
      }
    },
    codPanelHeader: function() {
      if (
        this.formulaire.cod.label === null ||
        this.formulaire.cod.label.length < 1
      ) {
        return this.$t("all_causes");
      } else {
        if (this.formulaire.cod.label.length > 4) {
          return this.formulaire.cod.label.slice(0, 4).join(", ") + ", ...";
        } else {
          return this.formulaire.cod.label.join(", ");
        }
      }
    },
    geoPanelHeader: function() {
      if (this.formulaire.geo.selectedGeoClass === null) {
        return this.$t("france_metropolitaine_et_drom");
      } else if (this.formulaire.geo.selectedGeoClass.length < 1) {
        return this.$t("france_metropolitaine_et_drom");
      } else if (this.formulaire.geo.selectedGeo.label === null) {
        return this.$t(this.formulaire.geo.selectedGeoClass);
      } else if (this.formulaire.geo.selectedGeo.label.length < 1) {
        return this.$t(this.formulaire.geo.selectedGeoClass);
      } else {
        if (this.formulaire.geo.selectedGeo.label.length > 4) {
          return (
            this.formulaire.geo.selectedGeo.label.slice(0, 4).join(", ") +
            ", ..."
          );
        } else {
          return this.formulaire.geo.selectedGeo.label.join(", ");
        }
      }
    }
  },
  methods: {
    date_input: x => parseInt(x, 10),
    calcul: function() {
      let formulaire = this.formulaire;
      this.erreur = false;
      const val = this;
      // Si les populations vivantes sont provisoires, on met un warning
      if ((this.formulaire.time_scope.range_1.date_1 === 2021 || 
           this.formulaire.time_scope.range_1.date_1 === 2022 ||
           this.formulaire.time_scope.range_1.date_2 === 2021 ||
           this.formulaire.time_scope.range_1.date_2 === 2022) && this.formulaire.measures !== "raw_number_of_death"){
        this.provisional_mortality_rate = true;
      } else {
        this.provisional_mortality_rate = false;
      }
      axios
        .get("bcmd/r/mortalityQuery", {
          params: {
            measures: formulaire.measures,
            standpop: formulaire.standpop,
            from: formulaire.time_scope.range_1.date_1,
            to: formulaire.time_scope.range_1.date_2,
            year_info: formulaire.time_scope.range_1.byYear,
            sex: formulaire.sex,
            code: formulaire.cod.code,
            age_info: formulaire.age.age,
            age_class: formulaire.age.selectedAgeClassLength,
            age_filter: formulaire.age.ageFilter,
            age_values: formulaire.age.selectedAge,
            agregration_1_24: val.agregration_1_24,
            geo_class: formulaire.geo.selectedGeoClass,
            geo_values: formulaire.geo.selectedGeo.code,
            lang: val.$i18n.locale
          }
        })
        .then(response => {
          if (Array.isArray(response.data)) {
            this.items = response.data;
            this.message_dimension_max = "";
            this.erreur_dimension_max = false;
          } else {
            this.items = [];
            this.message_dimension_max = response.data;
            this.erreur_dimension_max = true;
          }
          this.telechargement = false;
        })
        .catch(() => {
          this.erreur = true;
        });
    },
    cod_metadata: function() {
      this.error = false;
      if (this.items !== null) {
        if (this.formulaire.cod.code !== null) {
          if (this.formulaire.cod.code.length > 0) {
            axios
              .get("bcmd/r/codes_details", {
                params: {
                  code: this.formulaire.cod.code,
                  from: this.formulaire.time_scope.range_1.date_1,
                  to: this.formulaire.time_scope.range_1.date_2,
                  lang: this.$i18n.locale
                }
              })
              .then(response => {
                this.items_cod = Object.values(response.data);
              })
              .catch(() => {
                this.error = true;
              });
          } else {
            this.items_cod = null;
          }
        } else {
          this.items_cod = null;
        }
      } else {
        this.items_cod = null;
      }
    },
    downloadCsvData: function() {
      let formulaire = this.formulaire;
      this.erreur = false;
      const val = this;
      axios
        .get("bcmd/r/mortalityQuery_file", {
          params: {
            measures: formulaire.measures,
            standpop: formulaire.standpop,
            from: formulaire.time_scope.range_1.date_1,
            to: formulaire.time_scope.range_1.date_2,
            year_info: formulaire.time_scope.range_1.byYear,
            sex: formulaire.sex,
            code: formulaire.cod.code,
            age_info: formulaire.age.age,
            age_class: formulaire.age.selectedAgeClassLength,
            age_filter: formulaire.age.ageFilter,
            age_values: formulaire.age.selectedAge,
            agregration_1_24: val.agregration_1_24,
            geo_class: formulaire.geo.selectedGeoClass,
            geo_values: formulaire.geo.selectedGeo.code,
            lang: val.$i18n.locale
          }
        })
        .then(response => {
          window.location.href = response.request.responseURL;
          this.attente_telechargement = false;
          this.telechargement = true;
        })
        .catch(() => {
          this.erreur = true;
        });
    },
    updateDate: function(date) {
      this.formulaire.time_scope.range_1.date_1 = date.selectedDates[0];
      this.formulaire.time_scope.range_1.date_2 = date.selectedDates[1];
      this.formulaire.time_scope.range_1.byYear = date.byYear;
    },
    updateMeasures: function(measures) {
      this.formulaire.measures = measures.selectedMeasures;
      this.formulaire.standpop = measures.selectedStandPop;
      this.valid.measures = measures.valid;
    },
    updateSex: function(sex) {
      this.formulaire.sex = sex;
    },
    updateAge: function(age) {
      this.formulaire.age.age = age.age;
      this.formulaire.age.selectedAgeClassLength = age.selectedAgeClassLength;
      this.formulaire.age.ageFilter = age.ageFilter;
      this.formulaire.age.selectedAge = age.selectedAge;
    },
    updateGeo: function(geo) {
      this.formulaire.geo.selectedGeoClass = geo.selectedGeoClass;
      this.formulaire.geo.selectedGeo.code = geo.selectedGeo;
      if (geo.selectedGeo !== null) {
        this.erreur = false;
        axios
          .get("bcmd/r/geo_labels", {
            params: {
              code: geo.selectedGeo,
              scale: geo.selectedGeoClass
            }
          })
          .then(response => {
            this.formulaire.geo.selectedGeo.label = Object.values(
              response.data
            );
          })
          .catch(() => {
            this.erreur = true;
          });
      }
    },
    updateCod: function(cod) {
      this.formulaire.cod.code = cod;
      const self = this;
      this.erreur = false;
      axios
        .get("bcmd/r/eurostat_labels", {
          params: {
            code: cod,
            lang: self.$i18n.locale
          }
        })
        .then(response => {
          this.formulaire.cod.label = Object.values(response.data);
        })
        .catch(() => {
          this.erreur = true;
        });
    },
    tableheaders: function(data) {
      const val = this;
      if (data !== null && data !== undefined) {
        if (data[0] !== null && data[0] !== undefined) {
          return Object.keys(data[0]).map(function(item) {
            return {
              text: val.$i18n.tc(item, 0),
              value: item
            };
          });
        }
    }
   }
  },
  beforeMount() {},
  watch: {
    items: function() {
      this.cod_metadata();
      this.datatableOptions.page = 1;
    }
  }
}

</script>
<style>
@import "../assets/styles/main.css";
</style>
